// LaTeX des animations : ce que la validation refuse, et les options KaTeX
// que le lecteur DOIT utiliser. Deux défenses : la validation refuse les
// commandes dangereuses (liens, HTML, images, styles, définitions de macros),
// et le lecteur rend avec `trust: false` et des bornes (taille, expansions).

/**
 * Commandes refusées. Un nom de commande TeX s'arrête au premier caractère
 * qui n'est pas une lettre : `(?![a-zA-Z])` et non `\b` (pour qui `_` et les
 * chiffres font partie du mot — `\href1` passait). Les définitions de macros
 * (`\let`, `\def`…) sont refusées : elles permettraient de renommer une
 * commande interdite. Les commandes internes (`\@…`, `\…@…`) aussi.
 */
export const FORBIDDEN_TEX_RE =
  /\\(?:href|url|html[a-zA-Z]*|includegraphics|class|style|id|data|let|futurelet|def|gdef|edef|xdef|newcommand|renewcommand|providecommand|global|expandafter|csname|rule|raisebox|vrule|hrule|kern|mkern|hskip|vskip|char)(?![a-zA-Z])|\\[a-zA-Z]*@/;

/** Options KaTeX imposées au lecteur (et à la capture vidéo). */
export const JRAF_KATEX_OPTIONS = {
  trust: false,
  throwOnError: false,
  strict: 'ignore',
  maxExpand: 100,
  /** em : borne la taille des boîtes */
  maxSize: 10,
} as const;

export type DisplayTextPart = { kind: 'text'; value: string } | { kind: 'math'; value: string };

/**
 * Découpe d'un texte affiché en texte et formules `$…$`, IDENTIQUE à celle
 * du rendu (`splitMathText` de @jeraisonne/db/cards, test de parité) : une
 * formule tient sur une ligne et n'est pas vide, `\$` est un dollar
 * littéral, un `$` resté seul reste du texte. La validation doit voir
 * exactement les formules que KaTeX rendra.
 */
export function splitDisplayText(input: string): DisplayTextPart[] {
  const parts: DisplayTextPart[] = [];
  let text = '';
  let i = 0;
  const pushText = () => {
    if (text) parts.push({ kind: 'text', value: text });
    text = '';
  };
  while (i < input.length) {
    const ch = input[i];
    if (ch === '\\' && input[i + 1] === '$') {
      text += '$';
      i += 2;
      continue;
    }
    if (ch === '$') {
      let j = i + 1;
      while (j < input.length && input[j] !== '\n') {
        if (input[j] === '\\') {
          j += 2;
          continue;
        }
        if (input[j] === '$') break;
        j += 1;
      }
      const inner = j < input.length && input[j] === '$' ? input.slice(i + 1, j) : null;
      if (inner !== null && inner.trim()) {
        pushText();
        parts.push({ kind: 'math', value: inner.trim() });
        i = j + 1;
        continue;
      }
    }
    text += ch;
    i += 1;
  }
  pushText();
  return parts;
}

/** Formules `$…$` d'un texte affiché (sans les `$`), telles que le rendu les verra. */
export function mathSegments(text: string): string[] {
  return splitDisplayText(text)
    .filter((p) => p.kind === 'math')
    .map((p) => p.value);
}

export type TexProblem = 'forbidden' | 'dollar';

/** Problèmes d'une formule (LaTeX sans `$`). */
export function texProblems(tex: string): TexProblem[] {
  const out: TexProblem[] = [];
  if (FORBIDDEN_TEX_RE.test(tex)) out.push('forbidden');
  if (tex.includes('$')) out.push('dollar');
  return out;
}

export type DisplayTextProblem = 'unbalanced' | 'display_math' | 'forbidden';

/**
 * Problèmes d'un texte affiché (`$…$` permis, rendu par `MathText`) :
 * `$` non refermé, `$$` (une formule à part va dans un objet `formula`),
 * commande LaTeX interdite dans une formule.
 */
export function displayTextProblems(text: string): DisplayTextProblem[] {
  const out: DisplayTextProblem[] = [];
  // `$$` actif (hors `\$`) : une formule à part va dans un objet `formula`
  if (/\$\$/.test(text.replace(/\\\$/g, ''))) out.push('display_math');
  // même règle que `hasUnbalancedMath` des cartes : un `$` qui n'ouvre ni ne ferme rien
  if (
    splitDisplayText(text.replace(/\\\$/g, '')).some(
      (p) => p.kind === 'text' && p.value.includes('$'),
    )
  )
    out.push('unbalanced');
  if (mathSegments(text).some((m) => FORBIDDEN_TEX_RE.test(m))) out.push('forbidden');
  return out;
}
